import { Loader } from 'lucide-react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import type { ComponentProps, JSX } from 'react';

import { cn } from '~/utils';

export const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-(length:--text-md) font-bold transition-all',
    "disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
    'shrink-0 [&_svg]:shrink-0 outline-none',
    'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
    'cursor-pointer leading-[130%]',
  ),
  {
    variants: {
      variant: {
        default:
          'bg-illicitGreen-500 text-carbon-500 shadow-xs hover:bg-illicitGreen-450 active:bg-illicitGreen-600 disabled:bg-illicitGreen-500/40',
        destructive:
          'bg-desire-500 text-white shadow-xs hover:bg-desire-400 active:bg-desire-600 disabled:bg-desire-500/50 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline: cn(
          'border bg-white shadow-xs dark:bg-input/30 dark:border-input',
          'hover:text-accent-foreground dark:hover:bg-input/50',
          'border-illicitGreen-500 hover:bg-illicitGreen-100 active:bg-illicitGreen-200 disabled:opacity-50',
        ),
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        unstyled: 'bg-transparent h-[unset]',
      },
      size: {
        default: 'h-11.5 px-4 py-[12.5px] has-[>svg]:px-4',
        sm: 'h-9.5 px-4 py-[8.5px] has-[>svg]:px-4',
        xs: 'h-8 px-4 py-[7px] has-[>svg]:px-4',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface IButtonProps
  extends ComponentProps<'button'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export function Button({
  size,
  variant,
  className,
  type = 'button',
  asChild = false,
  loading = false,
  children,
  disabled,
  ...props
}: IButtonProps): JSX.Element {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      type={type}
      data-slot='button'
      disabled={disabled || loading}
      className={cn(
        buttonVariants({
          size,
          variant,
          className,
        }),
      )}
      {...props}
    >
      {loading ? (
        <>
          <Loader className='animate-spin' />
          {children}
        </>
      ) : (
        children
      )}
    </Comp>
  );
}
